import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  type MouseEventHandler,
  type ReactNode,
  type SyntheticEvent,
} from "react";
import styled from "styled-components";

import { MediaFrame } from "../MediaFrame";
import { resolveVideoFrameAlignment } from "./frameAlignment";
import type { VideoCrossOrigin, VideoFrameAlign, VideoHandle, VideoObjectFit } from "./types";

const DEFAULT_ASPECT_RATIO = 16 / 9;

const Media = styled.video<{ $objectFit: VideoObjectFit }>`
  display: block;
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.color.bg.surfaceSunken};
  object-fit: ${({ $objectFit }) => $objectFit};
`;

const Frame = styled(MediaFrame)<{ $frameAlign: VideoFrameAlign }>`
  align-self: ${({ $frameAlign }) => resolveVideoFrameAlignment($frameAlign)};
`;

export interface VideoFrameProps {
  src: string;
  poster?: string | null;
  controls?: boolean;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  playsInline?: boolean;
  preload?: "none" | "metadata" | "auto";
  crossOrigin?: VideoCrossOrigin;
  objectFit?: VideoObjectFit;
  aspectRatio?: number;
  bordered?: boolean;
  /** owning flex surface 안에서 media frame만 정렬한다. 기본 start. */
  frameAlign?: VideoFrameAlign;
  /**
   * `MediaFrame.maxHeight` passthrough — aspectRatio와 함께 주면 양축 viewport-fit.
   * 좁아진 frame은 `frameAlign`으로 정렬한다.
   */
  maxHeight?: string;
  overlay?: ReactNode;
  /**
   * 우상단 인터랙티브 액션(전체화면 등) — `MediaFrameActions` row로 렌더. `overlay`
   * (pointer-events: none 장식층)와 별개 레이어. zoom composition에서는 프레임이 transform
   * 안에 들어가므로 이 prop 대신 `ZoomPanWrapper.actions`로 줄 것.
   */
  actions?: ReactNode;
  "aria-label"?: string;
  onLoadedMetadata?: (event: SyntheticEvent<HTMLVideoElement, Event>) => void;
  onLoadedData?: (event: SyntheticEvent<HTMLVideoElement, Event>) => void;
  onTimeUpdate?: (event: SyntheticEvent<HTMLVideoElement, Event>) => void;
  onEnded?: () => void;
  onError?: (event: SyntheticEvent<HTMLVideoElement, Event>) => void;
  onClick?: MouseEventHandler<HTMLVideoElement>;
}

/**
 * 내부 표현(presentational) 프레임: 공용 틀 `MediaFrame` 위에 native `<video>`만 얹는
 * 특수화(RFC-media-frame). 재생/탐색 로직은 없고 ref를 그대로 `<video>`로 forward한다 — 공통
 * 핸들이 곧 엘리먼트(`VideoHandle`)다.
 * controlled 엔진은 `VideoSurface`가, 멀티채널 동기화는 `useSyncedVideoTimeline`이 이 엘리먼트를
 * 잡아 구동한다. 단일 controlled 경로와 그리드 경로가 같은 프레임을 조합하므로, 그리드가
 * `VideoSurface`의 controlled API를 우회해 엘리먼트만 꺼내 쓰던 계층 문제가 사라진다.
 */
export const VideoFrame = forwardRef<VideoHandle, VideoFrameProps>(
  (
    {
      src,
      poster,
      controls = false,
      autoPlay = false,
      muted = true,
      loop = false,
      playsInline = true,
      preload = "metadata",
      crossOrigin,
      objectFit = "contain",
      aspectRatio = DEFAULT_ASPECT_RATIO,
      bordered = true,
      frameAlign = "start",
      maxHeight,
      overlay,
      actions,
      "aria-label": ariaLabel,
      onLoadedMetadata,
      onLoadedData,
      onTimeUpdate,
      onEnded,
      onError,
      onClick,
    },
    ref,
  ) => {
    // 외부 ref와 병합할 local ref — src 복구 effect가 엘리먼트를 잡아야 한다.
    const localRef = useRef<HTMLVideoElement | null>(null);
    const setRef = useCallback(
      (node: HTMLVideoElement | null) => {
        localRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      },
      [ref],
    );

    // 미디어 자원 복구 — 멀티채널 타임라인 정리(useSyncedVideoTimeline)가 언마운트/재등록 시
    // `removeAttribute("src") + load()`로 src를 벗긴다. StrictMode 이중 마운트나 ref 재부착
    // 같은 "일시적 teardown"에서는 같은 엘리먼트가 곧바로 다시 붙는데, `src` prop 값이 그대로라
    // React 가 DOM src 를 다시 세팅하지 않아 빈 소스로 남아 metadata/재생이 죽는다. (재)마운트 후
    // DOM src 가 prop 과 다르면 다시 채워 재생이 끊기지 않게 한다(누수 수정은 유지).
    useEffect(() => {
      const video = localRef.current;
      if (!video || !src) return;
      // src 가 "실제로 벗겨진"(빈/없는) 경우에만 복구 — 정상 재생 중엔 절대 load() 를 다시
      // 부르지 않게 해 중복 로드/디코더 누적을 막는다. prop 이 바뀐 정상 교체는 React 가 이미
      // 새 src 를 커밋한 뒤라 attribute 가 채워져 있어 여기로 오지 않는다.
      const current = video.getAttribute("src");
      if (!current) {
        video.src = src;
        video.load();
      }
    });

    return (
      <Frame
        $frameAlign={frameAlign}
        aspectRatio={aspectRatio}
        bordered={bordered}
        maxHeight={maxHeight}
        overlay={overlay}
        actions={actions}
      >
        <Media
          ref={setRef}
          src={src}
          poster={poster ?? undefined}
          controls={controls}
          autoPlay={autoPlay}
          muted={muted}
          loop={loop}
          playsInline={playsInline}
          preload={preload}
          crossOrigin={crossOrigin}
          $objectFit={objectFit}
          aria-label={ariaLabel}
          onLoadedMetadata={onLoadedMetadata}
          onLoadedData={onLoadedData}
          onTimeUpdate={onTimeUpdate}
          onEnded={onEnded}
          onError={onError}
          onClick={onClick}
        />
      </Frame>
    );
  },
);

VideoFrame.displayName = "VideoFrame";
